'use client'

import { useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import { toast } from 'sonner'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  FileIcon,
  MoreHorizontalIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  ImageIcon,
  CheckIcon,
  XIcon,
  FolderIcon,
  SquarePercentIcon,
  PackageIcon,
  CircleDollarSignIcon,
  FolderOutputIcon,
  ScanQrCodeIcon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DeleteConfirmationDialog,
  ProductsFilters,
  LoadingState,
  SortableHeader,
  CategoryPath,
  ProductImportMenuItems,
  ProductExportMenuItems
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Badge } from '@/components/ui/badge'
import {
  formatDateTime,
  formatPriceGross,
  formatPriceNet,
  formatNumber,
  getCountryCodeFromLocale,
  getLocalizedValue,
  getResizedPictureUrl,
  textLimit,
  tOptional
} from '@/lib/utils'
import { useProducts, useDeleteProduct, useBulkDeleteProducts } from '@/hooks'
import { ProductsFilterParams } from '@/types/product'
import { useProductsStore, useMetaStore, useMeStore } from '@/store'
import { BulkDeleteResponse } from '@/types/general'
import { hasAnyPermission, hasPermission } from '@/lib/permissions'
import { SortEnum } from '@/enums'
import Link from 'next/link'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // Product selection hooks from Zustand store
  const selectedProductIds = useProductsStore(state => state.selectedProductIds)
  const toggleProductSelection = useProductsStore(state => state.toggleProductSelection)
  const selectAllProducts = useProductsStore(state => state.selectAllProducts)
  const deselectAllProducts = useProductsStore(state => state.deselectAllProducts)
  const isProductSelected = useProductsStore(state => state.isProductSelected)

  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { mutate: deleteProductMutate, isPending: isDeleting } = useDeleteProduct()
  const { mutate: bulkDeleteProductsMutate, isPending: isBulkDeleting } = useBulkDeleteProducts()

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'PRODUCTS', 'CREATE')
  const canShow = hasPermission(currentUser, 'PRODUCTS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'PRODUCTS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'PRODUCTS', 'DELETE')

  const filters = useMemo<ProductsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    const categoriesParam = params.get('categoryIds')
    const vehicleGroupsParam = params.get('vehicleGroupIds')
    const brandsParam = params.get('brandIds')
    const modelsParam = params.get('modelIds')
    const priceMinParam = params.get('priceMin')
    const priceMaxParam = params.get('priceMax')
    const stockMinParam = params.get('stockMin')
    const stockMaxParam = params.get('stockMax')

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      active: params.get('active') === 'true' ? 'true' : params.get('active') === 'false' ? 'false' : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined,
      categoryIds: categoriesParam ? categoriesParam.split(',').filter(Boolean) : undefined,
      vehicleGroupIds: vehicleGroupsParam ? vehicleGroupsParam.split(',').filter(Boolean) : undefined,
      brandIds: brandsParam ? brandsParam.split(',').filter(Boolean) : undefined,
      modelIds: modelsParam ? modelsParam.split(',').filter(Boolean) : undefined,
      priceMin: priceMinParam ? parseFloat(priceMinParam) : undefined,
      priceMax: priceMaxParam ? parseFloat(priceMaxParam) : undefined,
      stockMin: stockMinParam ? parseInt(stockMinParam) : undefined,
      stockMax: stockMaxParam ? parseInt(stockMaxParam) : undefined
    }
  }, [searchParams])

  const { data: productsData, isLoading, error, refetch, isFetching } = useProducts(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<ProductsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (
          (key === 'createdUsers' ||
            key === 'updatedUsers' ||
            key === 'categories' ||
            key === 'vehicleGroups' ||
            key === 'brands' ||
            key === 'models') &&
          Array.isArray(value)
        ) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products')
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleSort = (sortBy: string) => {
    const newSort = filters.sortBy === sortBy && filters.sort === SortEnum.ASC ? SortEnum.DESC : SortEnum.ASC
    handleFiltersChange({ sortBy, sort: newSort })
  }

  const handleView = (id: string) => {
    router.push(`/products/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/products/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteProductId(id)
  }

  const confirmDeleteProduct = () => {
    if (deleteProductId) {
      deleteProductMutate(deleteProductId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['products'] })
          toast.success(t('deleted_successfully'))
          setDeleteProductId(null)
        },
        onError: () => {
          toast.error(t('error_deleting'))
        }
      })
    }
  }

  const confirmBulkDeleteProducts = () => {
    if (selectedProductIds.length > 0) {
      bulkDeleteProductsMutate(selectedProductIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['products'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }
        }
      })
    }
  }

  const renderActionItems = (productId: string, inDropdown = true) => {
    const items = [
      ...(canShow
        ? [
            {
              label: t('product_details'),
              icon: EyeIcon,
              onClick: () => handleView(productId)
            }
          ]
        : []),
      ...(canUpdate
        ? [
            {
              label: t('edit_product'),
              icon: EditIcon,
              onClick: () => handleEdit(productId)
            }
          ]
        : []),
      ...(canDelete
        ? [
            {
              label: t('delete'),
              icon: Trash2Icon,
              onClick: () => handleDelete(productId),
              className: 'text-red-600 hover:text-red-700'
            }
          ]
        : [])
    ]

    if (inDropdown) {
      return items.map(item => (
        <DropdownMenuItem
          key={item.label}
          className={item.className}
          onSelect={e => {
            e.preventDefault()
            e.stopPropagation()
            item.onClick()
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            item.onClick()
          }}>
          <item.icon className="mr-2 h-4 w-4" />
          {item.label}
        </DropdownMenuItem>
      ))
    }

    return items.map(item => (
      <ContextMenuItem
        key={item.label}
        className={item.className}
        onSelect={e => {
          e.preventDefault()
          e.stopPropagation()
          item.onClick()
        }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          item.onClick()
        }}>
        <item.icon className="mr-2 h-4 w-4" />
        {item.label}
      </ContextMenuItem>
    ))
  }

  const handleBulkDelete = () => {
    setBulkDeleteConfirmOpen(true)
  }

  const handleSelectAll = () => {
    if (productsData?.items) {
      if (selectedProductIds.length === productsData.items.length) {
        deselectAllProducts()
      } else {
        selectAllProducts(productsData.items)
      }
    }
  }

  if (isLoading) {
    return <LoadingState isLoading={true}>{t('loading')}</LoadingState>
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('error')}</CardTitle>
          <CardDescription>{t('error_loading_record')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()}>{t('try_again')}</Button>
        </CardContent>
      </Card>
    )
  }

  if (!productsData) {
    return null
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <PackageIcon className="h-5 w-5" />
                {t('products')}
              </CardTitle>
              <CardDescription>{t('products_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/products/create" className="flex items-center">
                    <SquarePlusIcon />
                    <span className="hidden lg:flex">{t('create_product')}</span>
                  </Link>
                </Button>
              )}
              {(hasPermission(currentUser, 'PRODUCTS', 'IMPORT') ||
                hasPermission(currentUser, 'PRODUCTS', 'EXPORT')) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <FolderOutputIcon />
                      <span className="hidden items-center gap-2 lg:flex">{t('bulk_operations')}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {hasAnyPermission(currentUser, [
                      ['PRODUCTS', 'IMPORT'],
                      ['PRODUCTS', 'EXPORT']
                    ]) && (
                      <>
                        {hasPermission(currentUser, 'PRODUCTS', 'IMPORT') && (
                          <>
                            {ProductImportMenuItems.map((item, key) => (
                              <DropdownMenuItem key={key} className="flex flex-row justify-between gap-2">
                                <Link href={item.href} className="flex flex-row items-center justify-start gap-2">
                                  <item.icon />
                                  <span>{tOptional(t, item.label)}</span>
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </>
                        )}
                        {hasPermission(currentUser, 'PRODUCTS', 'EXPORT') && (
                          <>
                            {ProductExportMenuItems.map((item, key) => {
                              let href = item.href
                              if (href === '/products/export/excel/create' || href === '/products/export/pdf/create') {
                                const currentParams = searchParams.toString()
                                href = `${href}${currentParams ? `?${currentParams}` : ''}`
                              }

                              return (
                                <DropdownMenuItem key={key} className="flex flex-row justify-between gap-2">
                                  <Link href={href} className="flex flex-row items-center justify-start gap-2">
                                    <item.icon />
                                    <span>{tOptional(t, item.label)}</span>
                                  </Link>
                                </DropdownMenuItem>
                              )
                            })}
                          </>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {canDelete && selectedProductIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedProductIds.length})</span>
                  </div>
                </Button>
              )}
              <ProductsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error as any} onRetry={refetch} loadingRows={5}>
            {productsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          {canDelete && (
                            <Checkbox
                              checked={
                                productsData.items.length > 0 && selectedProductIds.length === productsData.items.length
                              }
                              onCheckedChange={handleSelectAll}
                              className="cursor-pointer"
                            />
                          )}
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t('picture')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-37.5">
                          <SortableHeader
                            sortBy="code"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center justify-start gap-2">
                              <ScanQrCodeIcon className="h-4 w-4" />
                              {t('code')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy={`translations.${locale}.title`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('title')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="categoryId"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <FolderIcon size={16} />
                              {t('category')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="priceNet"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <CircleDollarSignIcon size={16} />
                              {t('price_without_tax')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="priceGross"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <CircleDollarSignIcon size={16} />
                              {t('price_with_tax')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-right">
                          <SortableHeader
                            sortBy="discountRate"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <SquarePercentIcon size={16} />
                              {t('product_discount_rate')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="stock"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <SquarePercentIcon size={16} />
                              {t('stock')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <SortableHeader
                            sortBy="active"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CheckIcon className="h-4 w-4" />
                            {t('active')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productsData.items.map((product, index) => (
                          <ContextMenu key={product.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(product.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isProductSelected(product.id)}
                                        onCheckedChange={() => toggleProductSelection(product.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {product.mainPicture ? (
                                    <Image
                                      src={getResizedPictureUrl(product.mainPicture.path, 90, 90)}
                                      alt={product.mainPicture.name || ''}
                                      width={90}
                                      height={90}
                                      className="mx-auto max-h-22.5 w-auto border object-contain"
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <FileIcon className="text-muted-foreground h-10 w-10" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="ml-2">
                                    {product.code}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>
                                      {textLimit(getLocalizedValue(product, 'title', locale, defaultLocale), 120)}
                                    </span>
                                    <span className="text-muted-foreground text-xs">ID: {product.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <CategoryPath category={product.category} />
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatPriceNet(product.price, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatPriceGross(product.price, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {(product.discountRate ?? 0) > 0 ? `${product.discountRate}%` : '-'}
                                </TableCell>
                                <TableCell className="pr-5 text-right">{formatNumber(product.stock, locale)}</TableCell>
                                <TableCell className="text-center">
                                  {product.active ? (
                                    <CheckIcon className="mx-auto h-5 w-5 text-green-500" />
                                  ) : (
                                    <XIcon className="mx-auto h-5 w-5 text-red-500" />
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(product.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(product.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(product.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(product.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={productsData.page}
                  totalPages={productsData.pages}
                  totalItems={productsData.total}
                  pageSize={productsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteProductId}
          onCancel={() => setDeleteProductId(null)}
          onConfirm={confirmDeleteProduct}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteProducts}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
